/**
 * DataSource: Auditoria (audit_log browser) — GUPM-138
 * -----------------------------------------------------
 * Tira o `audit_log` do acesso direto ao Supabase que existia no M10
 * (violação de arquitetura). A UI da aba /auditoria consome só este
 * datasource; a derivação de rótulos/payload fica em `src/domain/auditoria`.
 *
 * Lê a view `audit_log_public` (GUPM-138): gate de perfil B no DB
 * (`current_app_user_profile()` → operador recebe 0 linhas) + redação
 * defensiva do segredo TOTP (`audit_secret_keys()`, denylist central do
 * GUPM-144). A base `audit_log` teve SELECT revogado para `authenticated`,
 * então este é o único caminho de leitura client-side. O `RoleGuard` na UI
 * é a 2ª camada (defense-in-depth).
 *
 * GUPM-335 (adendo 03/10): TODOS os filtros vão ao banco e a lista é paginada
 * por cursor `(ts, id)`. Antes vinham as 200 linhas mais recentes e a tela
 * filtrava entidade/ação/ator no navegador — em produção a 200ª linha era de
 * 1 dia e meio antes, e o filtro "Registro TARP" mostrava 0 de 53 em 30 dias.
 */

import { supabase } from '@/lib/supabase';
import type { AuditEntry } from '@/domain/auditoria';
import type { FiltroQuem } from '@/domain/auditoria/filtros';
import type { DataSourceMeta } from './types';

export interface AuditFilter {
  /** Nomes de tabela (uma opção do filtro pode cobrir mais de um). */
  entities?: string[];
  action?: string;
  /** `auth_user_id` do ator. */
  actorId?: string;
  quem?: FiltroQuem;
  from?: string; // ISO — inclusive
  to?: string; // ISO — inclusive (fim do dia)
}

/**
 * Posição da última linha exibida. `ts` é o texto CRU do banco: tem
 * microssegundos, que um `Date` do JS perderia — e com eles a paginação
 * repetiria ou pularia linhas gravadas no mesmo milissegundo.
 */
export interface AuditCursor {
  ts: string;
  id: string;
}

export interface AuditPage {
  entries: AuditEntry[];
  /** Nulo = não há mais linhas. */
  next: AuditCursor | null;
}

export interface AuditListOptions {
  after?: AuditCursor;
  pageSize?: number;
}

export interface AtorDaAuditoria {
  /** `auth_user_id`. */
  id: string;
  nome: string;
}

export interface AuditoriaDataSource extends DataSourceMeta {
  listEntries(filter: AuditFilter, opts?: AuditListOptions): Promise<AuditPage>;
  /** Pessoas com conta vinculada — opções do filtro de Ator. */
  listAtores(): Promise<AtorDaAuditoria[]>;
}

export const TAMANHO_DA_PAGINA = 100;

/** Shape do SELECT (não da tabela inteira) — colunas reais do audit_log. */
interface AuditLogRow {
  id: string;
  entity: string | null;
  entity_id: string | null;
  action: string;
  actor_id: string | null;
  payload: unknown;
  ts: string;
  // GUPM-335
  actor_email: string | null;
  payload_before: unknown;
  changed_fields: string[] | null;
  ip: string | null;
  ip_cadeia: string | null;
  user_agent: string | null;
  canal: string | null;
  conexao: string | null;
}

export const supabaseAuditoria: AuditoriaDataSource = {
  mode: 'manual',

  async listEntries(filter, opts = {}) {
    const tamanho = opts.pageSize ?? TAMANHO_DA_PAGINA;
    let q = supabase
      .from('audit_log_public')
      .select(
        'id, entity, entity_id, action, actor_id, payload, ts, actor_email, payload_before, changed_fields, ip, ip_cadeia, user_agent, canal, conexao',
      )
      .order('ts', { ascending: false })
      .order('id', { ascending: false })
      // Uma a mais só para saber se existe a próxima página.
      .limit(tamanho + 1);

    if (filter.entities?.length) q = q.in('entity', filter.entities);
    if (filter.action) q = q.eq('action', filter.action);
    if (filter.actorId) q = q.eq('actor_id', filter.actorId);
    if (filter.quem === 'pessoas') q = q.not('actor_id', 'is', null);
    if (filter.quem === 'sistema') q = q.is('actor_id', null);
    if (filter.from) q = q.gte('ts', filter.from);
    if (filter.to) q = q.lte('ts', filter.to);
    if (opts.after) {
      // Estritamente depois da última linha exibida na ordem (ts desc, id desc).
      // Aspas: o ts tem ":" e "+", que o PostgREST lê como sintaxe sem elas.
      const { ts, id } = opts.after;
      q = q.or(`ts.lt."${ts}",and(ts.eq."${ts}",id.lt.${id})`);
    }

    const { data, error } = await q.returns<AuditLogRow[]>();
    if (error) throw error;
    const todas = data ?? [];
    const rows = todas.slice(0, tamanho);
    const ultima = rows.at(-1);
    const next = todas.length > tamanho && ultima ? { ts: ultima.ts, id: ultima.id } : null;

    // Resolve nome do ator (auth_user_id → name) via view pública — mesma
    // fonte que o M10 antigo usava; view não expõe totp_secret_base32.
    const actorIds = [...new Set(rows.map((r) => r.actor_id).filter(Boolean))] as string[];
    const nameById = new Map<string, string>();
    if (actorIds.length > 0) {
      const { data: users, error: usersErr } = await supabase
        .from('app_users_public')
        .select('auth_user_id, name')
        .in('auth_user_id', actorIds);
      if (usersErr) throw usersErr;
      for (const u of users ?? []) {
        if (u.auth_user_id) nameById.set(u.auth_user_id, u.name);
      }
    }

    const entries = rows.map((r) => ({
      id: r.id,
      ts: new Date(r.ts),
      entity: r.entity,
      entityId: r.entity_id,
      action: r.action,
      actorId: r.actor_id,
      actorName: r.actor_id ? (nameById.get(r.actor_id) ?? null) : null,
      payload: r.payload,
      actorEmail: r.actor_email,
      payloadBefore: r.payload_before,
      changedFields: r.changed_fields,
      ip: r.ip,
      ipCadeia: r.ip_cadeia,
      userAgent: r.user_agent,
      canal: r.canal,
      conexao: r.conexao,
    }));
    return { entries, next };
  },

  async listAtores() {
    const { data, error } = await supabase
      .from('app_users_public')
      .select('auth_user_id, name')
      .not('auth_user_id', 'is', null)
      .order('name');
    if (error) throw error;
    return (data ?? [])
      .filter((u): u is { auth_user_id: string; name: string } => !!u.auth_user_id)
      .map((u) => ({ id: u.auth_user_id, nome: u.name }));
  },
};
